import { LOCALSTORAGE_TEAM_SELECTED_KEY } from "@/contexts/teams-provider";
// Value import into a module that only imports a *type* back from here, so the
// cycle is erased at build time and never exists at runtime.
import { linkedinCompanyHandle } from "@/lib/company-mention-reliability";
import { companyNamesAreConsistent } from "@/lib/company-name-matching";
import { digitsOnly } from "@/lib/string";
import { CompanyDTO } from "@shaarp/common";
import type { AxiosInstance } from "axios";

const SEARCH_RESULTS_LIMIT = 5;

// The module-level caches below live for the whole SPA session; cap them so a
// long session with many conversations can't grow them without bound.
const MAX_CACHE_ENTRIES = 500;

/**
 * The team currently selected in this browser tab — mirrors how `useApi`
 * attaches the `Team-Id` header (see `ui/src/hooks/useApi.ts`). Folded into
 * every resolution key so a company resolved (and enriched with the
 * requesting team's own notes/assignations) under one team is never served
 * from cache to another team after a switch.
 */
function currentTeamId(): string {
  return localStorage.getItem(LOCALSTORAGE_TEAM_SELECTED_KEY) || "";
}

/**
 * Sets `key` on `cache`, evicting the oldest entry once `MAX_CACHE_ENTRIES` is
 * reached (a plain `Map` iterates in insertion order, so `.keys().next()` is
 * the oldest survivor).
 */
function boundedSet<V>(cache: Map<string, V>, key: string, value: V): void {
  if (!cache.has(key) && cache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.set(key, value);
}

export type CompanyResolution =
  | { status: "company"; company: CompanyDTO }
  | { status: "candidates"; candidates: CompanyDTO[] }
  | { status: "notFound" }
  | { status: "insufficient" };

export type CompanyResolutionParams = {
  id?: string;
  siret?: string;
  siren?: string;
  domain?: string;
  /** The company's LinkedIn page URL — see `company-mention-reliability.ts`. */
  linkedin?: string;
  name?: string;
  city?: string;
};

// Memoizes resolutions by company identity so a company mentioned several times
// — or whose chip re-mounts while the message streams — is only resolved (and,
// for unknown companies, enriched) once per session.
const resolutionCache = new Map<string, Promise<CompanyResolution>>();

/**
 * Whether a resolved company carries enough identity to be worth showing. A
 * mention can resolve (by domain/siren) to an unenriched discovery stub whose
 * name — and most other fields — are null; there is nothing meaningful to
 * display, so the chip surfaces it as an inert "insufficient information" label
 * rather than opening an empty panel.
 */
export function hasUsableIdentity(company: CompanyDTO): boolean {
  return typeof company.name === "string" && company.name.trim().length > 0;
}

const INSUFFICIENT_CACHE_STORAGE_KEY = "company-mention-insufficient";
const INSUFFICIENT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// A mention found insufficient survives only in the in-memory cache above, so
// a reload forgets it and the next resolution re-runs the same lookup just to
// land on the same stub again. Persisting the verdict to localStorage — pruned
// and re-checked against a 1-day TTL rather than kept forever, since enrichment
// run server-side may eventually give the company a usable identity — skips it.
function readInsufficientCache(): Record<string, number> {
  try {
    const raw = localStorage.getItem(INSUFFICIENT_CACHE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeInsufficientCache(cache: Record<string, number>): void {
  try {
    localStorage.setItem(INSUFFICIENT_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Storage unavailable/full — the in-memory session cache still applies.
  }
}

/**
 * Whether a mention is already known (this session or a prior one, within the
 * TTL) to resolve to a stub with no usable identity.
 */
function isKnownInsufficient(key: string): boolean {
  const expiresAt = readInsufficientCache()[key];
  return typeof expiresAt === "number" && Date.now() < expiresAt;
}

function markInsufficient(key: string): void {
  const now = Date.now();
  const cache = readInsufficientCache();
  cache[key] = now + INSUFFICIENT_CACHE_TTL_MS;
  // Prune expired entries opportunistically so the cache doesn't grow forever.
  for (const cachedKey of Object.keys(cache)) {
    if (cache[cachedKey] <= now) delete cache[cachedKey];
  }
  writeInsufficientCache(cache);
}

/**
 * Keys a mention by its identity. The name is the label the user clicks, so it
 * anchors the key whenever present: two differently-named mentions must never
 * collapse onto one entry just because they carry the same domain (the assistant
 * sometimes cites several companies with a shared directory/aggregator domain it
 * doesn't actually own). A siren/domain is appended only to separate genuine
 * homonyms (same name, different company). An explicit id always wins.
 */
export function companyResolutionKey({
  id,
  siret,
  siren,
  domain,
  linkedin,
  name,
  city,
}: CompanyResolutionParams): string | null {
  const teamPrefix = `team:${currentTeamId()}|`;
  if (id) return `${teamPrefix}id:${id}`;

  const normalizedName = name?.trim().toLowerCase();
  const normalizedSiret = siret?.trim();
  const normalizedSiren = siren?.trim();
  const normalizedDomain = domain?.trim().toLowerCase();
  const normalizedLinkedin = linkedinCompanyHandle(linkedin);
  const normalizedCity = city?.trim().toLowerCase();

  if (normalizedName) {
    // siret pins one establishment, and a city hint separates two
    // establishments of the same SIREN — so both come before siren/domain,
    // which would otherwise collapse distinct establishments onto one entry.
    // A SIREN beside the city separates two homonyms in one town.
    if (normalizedSiret)
      return `${teamPrefix}name:${normalizedName}|siret:${normalizedSiret}`;
    if (normalizedCity)
      return normalizedSiren
        ? `${teamPrefix}name:${normalizedName}|city:${normalizedCity}|siren:${normalizedSiren}`
        : `${teamPrefix}name:${normalizedName}|city:${normalizedCity}`;
    if (normalizedSiren)
      return `${teamPrefix}name:${normalizedName}|siren:${normalizedSiren}`;
    if (normalizedDomain)
      return `${teamPrefix}name:${normalizedName}|domain:${normalizedDomain}`;
    if (normalizedLinkedin)
      return `${teamPrefix}name:${normalizedName}|linkedin:${normalizedLinkedin}`;
    return `${teamPrefix}name:${normalizedName}`;
  }

  if (normalizedSiret) return `${teamPrefix}siret:${normalizedSiret}`;
  if (normalizedSiren) return `${teamPrefix}siren:${normalizedSiren}`;
  if (normalizedDomain) return `${teamPrefix}domain:${normalizedDomain}`;
  if (normalizedLinkedin) return `${teamPrefix}linkedin:${normalizedLinkedin}`;
  return null;
}

/**
 * Resolves a company mention, kicking off (and caching) the work the first time
 * the mention appears so a later click can open the side panel instantly.
 *
 * Strategies are tried by discriminator strength until one yields a company or
 * candidates:
 * - id     → `GET /company/available/:id`
 * - siren  → `GET /companies/available?search=` (one opens, several disambiguate),
 *            then `GET /companies/resolve?siren=`
 * - domain → same, then `GET /companies/resolve?domain=`
 * - linkedin → same, then `GET /companies/resolve?linkedin=`
 * - name   → same, then `GET /companies/resolve?name=`
 *
 * A siren/domain match is only accepted when its company is consistent with the
 * cited name (see `companyNamesAreConsistent`); otherwise resolution falls through to
 * the name, so a directory domain the assistant doesn't own can't hijack the
 * mention. The registry fallback finds & enriches a company absent from the
 * team's database (e.g. one the assistant cited in chat).
 */
export function resolveCompany(
  api: AxiosInstance,
  key: string,
  params: CompanyResolutionParams,
): Promise<CompanyResolution> {
  const cached = resolutionCache.get(key);
  if (cached) return cached;

  // A mention already found insufficient (this session or a prior one, within
  // the TTL) needs no re-fetch: it can only have resolved to the same stub.
  if (isKnownInsufficient(key)) {
    const insufficient = Promise.resolve<CompanyResolution>({
      status: "insufficient",
    });
    boundedSet(resolutionCache, key, insufficient);
    return insufficient;
  }

  const pending = doResolveCompany(api, params)
    .then((resolution): CompanyResolution => {
      if (resolution.status === "company") {
        if (!hasUsableIdentity(resolution.company)) {
          markInsufficient(key);
          return { status: "insufficient" };
        }
      }
      return resolution;
    })
    .catch((error) => {
      // Never cache transient failures — let a later click retry.
      resolutionCache.delete(key);
      throw error;
    });
  boundedSet(resolutionCache, key, pending);
  return pending;
}

async function doResolveCompany(
  api: AxiosInstance,
  { id, siret, siren, domain, linkedin, name, city }: CompanyResolutionParams,
): Promise<CompanyResolution> {
  if (id) {
    const { data } = await api.get<CompanyDTO>(`/company/available/${id}`);
    return ensureQualified(api, { status: "company", company: data });
  }

  // siret pins a single establishment — the most precise discriminator.
  if (siret) {
    const resolution = await resolveViaSearch(api, siret, { siret });
    if (resolution && isConsistentWithName(resolution, name))
      return ensureQualified(api, resolution);
  }

  // A name + city opens the establishment at that location, so it is tried
  // before domain/siren — which resolve to the head office and would otherwise
  // win the name-consistency check and open the wrong establishment.
  if (name && city) {
    const found = await resolveViaSearch(api, `${name} ${city}`, {
      name,
      city,
    });
    const resolution = siren ? ofSiren(found, siren) : found;
    if (resolution && isConsistentWithName(resolution, name))
      return ensureQualified(api, resolution);
  }

  // domain/siren are precise for the company but resolve to the head office;
  // only trusted when consistent with the cited name. The assistant sometimes
  // attaches a directory/aggregator domain it doesn't own (e.g. the regional
  // tourism site that merely lists a farm) to several distinct companies; left
  // unchecked, every one would resolve to that site's owner. On a mismatch we
  // fall through to the weaker discriminator, then to the name.
  if (domain) {
    const resolution = await resolveViaSearch(api, domain, { domain });
    if (resolution && isConsistentWithName(resolution, name))
      return ensureQualified(api, resolution);
  }

  if (siren) {
    const resolution = await resolveViaSearch(api, siren, { siren });
    if (resolution && isConsistentWithName(resolution, name))
      return ensureQualified(api, resolution);
  }

  // Weakest of the discriminators, and the only one that can cost a paid lookup
  // server-side, so it is tried last: a LinkedIn page opens the head office,
  // exactly like a domain, and the assistant only carries one when it found no
  // website at all.
  // Straight to the server, skipping the search the other discriminators start
  // with: for them that search is an exact lookup, for a handle it would be a
  // name search, and a homonym found that way would win over the one company
  // the page designates.
  if (linkedinCompanyHandle(linkedin)) {
    const resolution = await resolveOnServer(api, { linkedin });
    if (resolution && isConsistentWithName(resolution, name))
      return ensureQualified(api, resolution);
  }

  if (name) {
    const resolution = await resolveViaSearch(api, name, { name });
    if (resolution) return ensureQualified(api, resolution);
  }

  return { status: "notFound" };
}

/**
 * What of a name + city resolution belongs to `siren`: with a SIREN, the
 * mention designates one of that company's establishments, never a homonym's
 * in the same town. Null when none does, so the SIREN itself is tried next.
 */
function ofSiren(
  resolution: CompanyResolution | null,
  siren: string,
): CompanyResolution | null {
  const wanted = digitsOnly(siren);
  if (!wanted) return resolution;
  const isOfSiren = ({ business }: CompanyDTO): boolean =>
    digitsOnly(business?.siren ?? "") === wanted ||
    digitsOnly(business?.siret ?? "").startsWith(wanted);

  if (resolution?.status === "company") {
    return isOfSiren(resolution.company) ? resolution : null;
  }
  if (resolution?.status !== "candidates") return null;
  const candidates = resolution.candidates.filter(isOfSiren);
  if (candidates.length === 1) {
    return { status: "company", company: candidates[0] };
  }
  return candidates.length > 1 ? { status: "candidates", candidates } : null;
}

// Companies the assistant cites that aren't yet in the team's database are
// enriched to QUALIFIED (level 2) by `/companies/resolve` before the side panel
// opens. A company already in the database, by contrast, is returned by the
// search lookup at whatever depth it was stored — often lower — so its panel
// looks comparatively empty. This top-up brings such database hits up to the
// same QUALIFIED depth.
const QUALIFIED_ENRICHMENT_LEVEL = 2;

/**
 * Brings a resolved company up to QUALIFIED level when the database returned it
 * below that depth, so a company already known to the team shows the same info
 * as one freshly resolved through the registry.
 *
 * The top-up is driven by the resolved company's own canonical identifier
 * (siret preferred, then siren, then domain) — all database-deduped
 * server-side, so no homonym can be substituted. A company identified only by
 * name is left as-is:
 * re-resolving by name could match a different entity. Enrichment is
 * best-effort — a failure returns the original resolution rather than dropping
 * it. As `resolveCompany` runs (and caches) on the mention's first appearance,
 * this work usually completes in the background before the user clicks.
 */
async function ensureQualified(
  api: AxiosInstance,
  resolution: CompanyResolution,
): Promise<CompanyResolution> {
  if (resolution.status !== "company") return resolution;

  const { company } = resolution;
  if ((company.enrichmentLevel ?? 0) >= QUALIFIED_ENRICHMENT_LEVEL) {
    return resolution;
  }

  const siret = company.business?.siret?.trim();
  const siren = company.business?.siren?.trim();
  const domain = company.domain?.trim();
  // Prefer the SIRET so a company resolved to a specific establishment is
  // topped up as that same establishment; re-resolving by siren/domain would
  // fall back to the head office and overwrite it.
  const params = siret
    ? { siret }
    : siren
      ? { siren }
      : domain
        ? { domain }
        : null;
  if (!params) return resolution;

  try {
    const { data } = await api.get<CompanyDTO>(`/companies/resolve`, { params });
    return { status: "company", company: data };
  } catch {
    return resolution;
  }
}

/**
 * Searches the team's database by a discriminator — the `available` endpoint
 * auto-detects name/siren/domain — and, when nothing matches, resolves &
 * enriches via the external registry.
 *
 * Returns null when the strategy yields nothing, so the caller can fall through
 * to a weaker discriminator; a 404 from the registry is "nothing", not an error.
 */
async function resolveViaSearch(
  api: AxiosInstance,
  search: string,
  resolveParams: ServerResolveParams,
): Promise<CompanyResolution | null> {
  const { data } = await api.get<CompanyDTO[]>(`/companies/available`, {
    params: { search, limit: SEARCH_RESULTS_LIMIT },
  });

  // A stored company with no name is the placeholder the domain lookup creates
  // when its provider knows nothing about the site. It can neither be displayed
  // nor opened, so it must not end the search: dropping it lets the chain fall
  // through to the next discriminator — usually the cited name, which finds the
  // real company.
  const named = data.filter(hasUsableIdentity);

  if (named.length === 1) return { status: "company", company: named[0] };
  if (named.length > 1) return { status: "candidates", candidates: named };

  return resolveOnServer(api, resolveParams);
}

type ServerResolveParams = {
  readonly siret?: string;
  readonly siren?: string;
  readonly domain?: string;
  readonly linkedin?: string;
  readonly name?: string;
  readonly city?: string;
};

/**
 * `GET /companies/resolve` — the team's database, then the external registry,
 * which enriches a company Shaarp did not know yet. Null when nothing matches:
 * a 404 there is "nothing", not an error.
 */
async function resolveOnServer(
  api: AxiosInstance,
  params: ServerResolveParams,
): Promise<CompanyResolution | null> {
  try {
    const { data } = await api.get<CompanyDTO>(`/companies/resolve`, {
      params,
    });
    return { status: "company", company: data };
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

/**
 * Applies the name-consistency guard (shared with the backend reconciliation
 * service via `@shaarp/common`, so both sides enforce the same policy) to a
 * discriminator-based resolution. Only a concrete `company` is checked;
 * `candidates` are left for the user to pick from.
 */
function isConsistentWithName(
  resolution: CompanyResolution,
  citedName: string | undefined,
): boolean {
  if (resolution.status !== "company") return true;
  return companyNamesAreConsistent(citedName, resolution.company.name);
}
